import { useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';

function resolveServerOriginFromCurrentHost(): string {
  const { protocol, hostname, port } = window.location;

  if (port) {
    return `${protocol}//${hostname}:3001`;
  }

  const forwardedHostname = hostname.replace(/-(\d{4,5})\./, '-3001.');
  if (forwardedHostname !== hostname) {
    return `${protocol}//${forwardedHostname}`;
  }

  return `${protocol}//${hostname}:3001`;
}

function normalizeSocketUrl(configuredUrl: string): string | undefined {
  if (!configuredUrl) return undefined;

  try {
    const parsed = new URL(configuredUrl, window.location.origin);
    const isConfiguredLocalhost = ['localhost', '127.0.0.1'].includes(parsed.hostname);
    const isCurrentLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname);

    if (isConfiguredLocalhost && !isCurrentLocalhost) {
      return resolveServerOriginFromCurrentHost();
    }

    if (window.location.protocol === 'https:' && parsed.protocol === 'http:') {
      parsed.protocol = 'https:';
    }

    return parsed.toString().replace(/\/$/, '');
  } catch {
    return configuredUrl;
  }
}

export function useSocket(role: 'instructor' | 'participant') {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    const socketUrl = normalizeSocketUrl((import.meta.env.VITE_SOCKET_URL || '').trim());

    const newSocket = io(socketUrl, {
      path: '/socket.io',
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
    });

    newSocket.on('connect', () => {
      console.log('Socket connected');
      newSocket.emit('identify', { role });
      setIsConnected(true);
    });

    newSocket.on('disconnect', () => {
      console.log('Socket disconnected');
      setIsConnected(false);
    });

    newSocket.on('connect_error', (error: any) => {
      console.error('Connection error:', error);
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, [role]);

  return { socket, isConnected };
}
